import { sha256 } from '@noble/hashes/sha256'
import { bytesToHex } from '@noble/hashes/utils'
import { getPublicKeyAsync } from '@noble/ed25519'

export const IDENTITY_VERSION = 1
const SEED_BYTES = 32

export interface CanonicalKeyRingEntry {
    kid: string
    pk: string
    source: 'primary' | 'imported' | 'invite'
    addedAt: string
    blobId?: string
}

export interface CanonicalIdentity {
    v: number
    primarySeed: string
    keyRing: CanonicalKeyRingEntry[]
}

function toBase64Url(bytes: Uint8Array): string {
    let binary = ''
    bytes.forEach((byte) => { binary += String.fromCharCode(byte) })
    const b64 = btoa(binary)
    return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

function fromBase64Url(value: string): Uint8Array {
    const b64 = value.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (value.length % 4)) % 4)
    const binary = atob(b64)
    const out = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i)
    return out
}

async function derivePkFromSeed(seed: Uint8Array): Promise<Uint8Array> {
    const pk = await getPublicKeyAsync(seed)
    return Uint8Array.from(pk)
}

export async function deriveKidFromPk(pk: Uint8Array): Promise<string> {
    const digest = sha256(pk)
    return toBase64Url(digest.slice(0, 16))
}

export async function createKeyRingEntryFromSeed(seed: Uint8Array, source: CanonicalKeyRingEntry['source'] = 'primary'): Promise<CanonicalKeyRingEntry> {
    const pk = await derivePkFromSeed(seed)
    return {
        kid: await deriveKidFromPk(pk),
        pk: toBase64Url(pk),
        source,
        addedAt: new Date().toISOString(),
    }
}

export async function createCanonicalIdentity(primarySeed?: Uint8Array): Promise<CanonicalIdentity> {
    const seed = primarySeed ?? crypto.getRandomValues(new Uint8Array(SEED_BYTES))
    const primary = await createKeyRingEntryFromSeed(seed, 'primary')
    return {
        v: IDENTITY_VERSION,
        primarySeed: toBase64Url(seed),
        keyRing: [primary],
    }
}

export function validateCanonicalIdentity(identity: CanonicalIdentity): void {
    if (identity.v !== IDENTITY_VERSION) throw new Error('Unsupported identity version')
    const primarySeed = fromBase64Url(identity.primarySeed)
    if (primarySeed.length !== SEED_BYTES) throw new Error('Invalid primary seed length')
    if (!Array.isArray(identity.keyRing) || identity.keyRing.length === 0) throw new Error('keyRing must have at least one key')

    const uniqueKids = new Set<string>()
    let primaryCount = 0
    for (const entry of identity.keyRing) {
        if (!entry.kid || !entry.pk) throw new Error('keyRing entries must include kid and pk')
        if (uniqueKids.has(entry.kid)) throw new Error(`Duplicate kid: ${entry.kid}`)
        uniqueKids.add(entry.kid)
        if (entry.source === 'primary') primaryCount += 1
    }

    if (primaryCount !== 1) throw new Error('Identity must contain exactly one primary key')
}

export async function assertKeyDerivations(identity: CanonicalIdentity): Promise<void> {
    const primaryEntry = identity.keyRing.find((entry) => entry.source === 'primary')
    if (!primaryEntry) throw new Error('Missing primary key entry')

    const primarySeed = fromBase64Url(identity.primarySeed)
    const primaryPk = await derivePkFromSeed(primarySeed)
    const primaryKid = await deriveKidFromPk(primaryPk)
    if (primaryEntry.pk !== toBase64Url(primaryPk)) throw new Error('Primary pk does not match primarySeed')
    if (primaryEntry.kid !== primaryKid) throw new Error('Primary kid does not match derived pk')

    for (const entry of identity.keyRing) {
        const pkBytes = fromBase64Url(entry.pk)
        const derivedKid = await deriveKidFromPk(pkBytes)
        if (entry.kid !== derivedKid) throw new Error(`kid mismatch for key ${bytesToHex(pkBytes.slice(0, 4))}`)
    }
}

export async function normalizeCanonicalIdentity(input: CanonicalIdentity & { keyRing: Array<CanonicalKeyRingEntry & { seed?: string }> }): Promise<CanonicalIdentity> {
    type ExtendedEntry = CanonicalKeyRingEntry & { seed?: string }
    const primary: ExtendedEntry | undefined = input.keyRing.find((entry) => entry.source === 'primary')
    let primarySeed = input.primarySeed

    if (fromBase64Url(primarySeed).length !== SEED_BYTES) {
        if (!primary?.seed) throw new Error('Invalid primary seed and no legacy seed to migrate')
        primarySeed = primary.seed
    }

    const keyRing: CanonicalKeyRingEntry[] = []
    for (const entry of input.keyRing) {
        if (entry.pk) {
            keyRing.push({ kid: entry.kid, pk: entry.pk, source: entry.source, addedAt: entry.addedAt, blobId: entry.blobId })
            continue
        }

        if (!entry.seed) throw new Error(`Key entry ${entry.kid} is missing pk`)
        const derived = await createKeyRingEntryFromSeed(fromBase64Url(entry.seed), entry.source)
        keyRing.push({ ...derived, addedAt: entry.addedAt, blobId: entry.blobId })
    }

    return {
        v: input.v,
        primarySeed,
        keyRing,
    }
}

export function decodeBase64Url(value: string): Uint8Array {
    return fromBase64Url(value)
}

export function encodeBase64Url(value: Uint8Array): string {
    return toBase64Url(value)
}
